/**
 * Admin Dashboard Page
 *
 * Overview page for the admin panel showing system statistics
 * and quick access to management sections.
 */

import {
  Alert,
  AlertDescription,
  Button,
  Card,
  Grid,
  Heading,
  Inline,
  PageHeader,
  Skeleton,
  Stack,
  Text,
} from '@cortex/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  ArrowRight,
  Building2,
  FolderKanban,
  TrendingUp,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { EnvironmentConfig } from '@/components/admin/EnvironmentConfig';
import { useApi } from '@/hooks/use-api';

export const Route = createFileRoute('/_app/admin/')({
  component: AdminDashboardPage,
});

interface DashboardStats {
  organizationCount: number;
  userCount: number;
  projectCount: number;
  requestCount: number;
}

function AdminDashboardPage() {
  const navigate = useNavigate();
  const getApi = useApi();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchStats = useCallback(async () => {
    try {
      const api = await getApi();
      const response = await api.admin.stats.$get();
      if (!response.ok) {
        throw new Error('Failed to fetch stats');
      }
      const json = await response.json();
      if ('data' in json) {
        setStats(json.data as unknown as DashboardStats);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  }, [getApi]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statCards = [
    {
      title: 'Organizations',
      value: stats?.organizationCount ?? 0,
      icon: Building2,
      href: '/admin/organizations' as const,
      color: 'text-info bg-info/10',
    },
    {
      title: 'Users',
      value: stats?.userCount ?? 0,
      icon: Users,
      href: '/admin/users' as const,
      color: 'text-success bg-success/10',
    },
    {
      title: 'Projects',
      value: stats?.projectCount ?? 0,
      icon: FolderKanban,
      href: '/admin/projects' as const,
      color: 'text-primary bg-primary/10',
    },
    {
      title: 'Requests',
      value: stats?.requestCount ?? 0,
      icon: TrendingUp,
      href: '/admin' as const,
      color: 'text-warning bg-warning/10',
    },
  ];

  return (
    <Stack gap="xl" className="p-6">
      {/* Page Header */}
      <PageHeader
        title="Admin Dashboard"
        subtitle="Overview of your Cortex instance"
      />

      {/* Error State */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            {error}
            <Button
              variant="ghost"
              size="sm"
              onClick={fetchStats}
              className="mt-2"
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* Stats Grid */}
      <Grid gap="lg" className="sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((card) => (
          <Card key={card.title} className="p-6">
            <Inline justify="between">
              <Stack className={`rounded-lg p-2 ${card.color}`}>
                <card.icon className="h-5 w-5" />
              </Stack>
              {card.href !== '/admin' && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate({ to: card.href })}
                  className="gap-1"
                >
                  View
                  <ArrowRight className="h-3 w-3" />
                </Button>
              )}
            </Inline>
            <Stack gap="xs" className="mt-4">
              {loading ? (
                <Skeleton className="h-8 w-16" />
              ) : (
                <Text size="3xl" weight="bold">
                  {card.value.toLocaleString()}
                </Text>
              )}
              <Text color="muted" size="sm">
                {card.title}
              </Text>
            </Stack>
          </Card>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Card className="p-6">
        <Heading level={2} className="text-lg font-semibold">
          Quick Actions
        </Heading>
        <Inline wrap className="mt-4 gap-3">
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/admin/organizations' })}
            className="gap-2"
          >
            <Building2 className="h-4 w-4" />
            Manage Organizations
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/admin/users' })}
            className="gap-2"
          >
            <Users className="h-4 w-4" />
            Manage Users
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate({ to: '/admin/projects' })}
            className="gap-2"
          >
            <FolderKanban className="h-4 w-4" />
            Manage Projects
          </Button>
        </Inline>
      </Card>

      {/* System Info */}
      <Card className="p-6">
        <Heading level={2} className="text-lg font-semibold">
          System Information
        </Heading>
        <Grid gap="md" className="mt-4 sm:grid-cols-2 lg:grid-cols-3">
          <Stack gap="xs">
            <Text color="muted" size="sm">
              Environment
            </Text>
            <Text weight="medium">{import.meta.env.MODE || 'development'}</Text>
          </Stack>
          <Stack gap="xs">
            <Text color="muted" size="sm">
              Version
            </Text>
            <Text weight="medium">1.0.0</Text>
          </Stack>
          <Stack gap="xs">
            <Text color="muted" size="sm">
              Database
            </Text>
            <Text weight="medium">Supabase (PostgreSQL)</Text>
          </Stack>
        </Grid>
      </Card>

      {/* Environment Configuration */}
      <EnvironmentConfig />
    </Stack>
  );
}
